/**
 * Analytics CSV export — builds the file for a given Analytics tab.
 *
 * Serialises the rows the tab has ALREADY rendered. Nothing here re-fetches,
 * re-sorts or re-slices, and the totals line is passed in rather than recomputed,
 * so the file always matches what was on screen when the button was pressed.
 */

import { exportToCSV } from './reportHelpers';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ── cell formatters — plain numbers only, no separators, no unit suffixes ── */
const count = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n)) : '';
};

const weight = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '';
};

const text = (v) => (v === null || v === undefined ? '' : String(v));

/* ── per-tab column definitions ───────────────────────────────────────── */
const TABS = {
  items: {
    name: 'Items',
    headers: ['Part number', 'Item', 'Cases', 'Units', 'Weight (lb)', 'Pallets'],
    row: (r) => [
      text(r.part_number),
      text(r.item_name),
      count(r.total_cases),
      count(r.total_units),
      weight(r.total_weight),
      count(r.pallet_count),
    ],
    totals: (t) => [
      'Totals', '',
      count(t.cases), count(t.units), weight(t.weight), count(t.pallets),
    ],
  },

  customers: {
    name: 'Customers',
    headers: ['Customer', 'Shipments', 'Cases', 'Units', 'Weight (lb)'],
    row: (r) => [
      text(r.customer_name),
      count(r.shipment_count),
      count(r.total_cases),
      count(r.total_units),
      weight(r.total_weight),
    ],
    totals: (t) => [
      'Totals',
      count(t.shipments), count(t.cases), count(t.units), weight(t.weight),
    ],
  },

  shipments: {
    name: 'Shipments',
    headers: ['Month', 'Shipments', 'Weight (lb)',
              'Shipments (prior year)', 'Weight (prior year)'],
    row: (r) => [
      text(r.month_label),
      count(r.shipment_count),
      weight(r.total_weight),
      count(r.prev_shipment_count),
      weight(r.prev_total_weight),
    ],
    totals: (t) => [
      'Totals',
      count(t.shipments), weight(t.weight),
      count(t.prevShipments), weight(t.prevWeight),
    ],
  },

  seasons: {
    name: 'Seasons',
    headers: ['Year', ...MONTHS, 'Year total'],
    row: (r) => [
      text(r.year),
      ...MONTHS.map((_, i) => count((r.months || [])[i])),
      count(r.total),
    ],
    totals: (t) => [
      'Totals',
      ...MONTHS.map((_, i) => count((t.months || [])[i])),
      count(t.total),
    ],
  },
};

/* ── header block ─────────────────────────────────────────────────────── */
const BASIS_LABEL = {
  shipped: 'Shipped only',
  all: 'All (including unshipped)',
};

const pad2 = (n) => String(n).padStart(2, '0');

const stamp = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
  ` ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const buildPreamble = (tabName, filters) => {
  const { dateFrom = '', dateTo = '', customerName, basis } = filters || {};
  return [
    `Montreal Kosher — Analytics: ${tabName}`,
    `Date range: ${dateFrom} to ${dateTo}` +
      `  |  Customer: ${customerName || 'All customers'}` +
      `  |  Basis: ${BASIS_LABEL[basis] || BASIS_LABEL.shipped}` +
      `  |  Exported: ${stamp()}`,
  ];
};

/* ── file name ────────────────────────────────────────────────────────── */
const buildFilename = (tabId, filters) => {
  const { dateFrom = '', dateTo = '', basis } = filters || {};
  // exportToCSV appends _YYYY-MM-DD.csv — do not add a date here.
  return `analytics-${tabId}_${basis === 'all' ? 'all' : 'shipped'}_${dateFrom}_${dateTo}`;
};

/* ── shape helper ─────────────────────────────────────────────────────── */
/**
 * Reshape the Seasons heatmap's { years, cells } into export rows.
 * A pure rearrangement of the rendered figures — no recalculation.
 */
export const seasonalRowsFromHeatmap = (data) => {
  if (!data || !Array.isArray(data.years)) return [];
  const cells = data.cells || {};
  return data.years.map(year => {
    const months = MONTHS.map((_, i) => Number(cells[`${year}-${i + 1}`]) || 0);
    return { year, months, total: months.reduce((a, b) => a + b, 0) };
  });
};

/* ── the export ───────────────────────────────────────────────────────── */
/**
 * Build and download the CSV for one Analytics tab.
 *
 * @param {string} tabId   'items' | 'customers' | 'shipments' | 'seasons'
 * @param {object} params
 *   rows    — the rows the tab is rendering, in render order
 *   totals  — the on-screen totals line (optional; omitted from the file if absent)
 *   filters — { dateFrom, dateTo, customerName, basis }
 * @returns {boolean} true when a file was produced, false when there was nothing to export
 */
export const exportAnalyticsTab = (tabId, { rows, totals, filters } = {}) => {
  const tab = TABS[tabId];
  if (!tab) return false;

  const dataRows = (Array.isArray(rows) ? rows : []).map(tab.row);
  if (totals) dataRows.push(tab.totals(totals));
  if (dataRows.length === 0) return false;

  exportToCSV(
    tab.headers,
    dataRows,
    buildFilename(tabId, filters),
    buildPreamble(tab.name, filters)
  );
  return true;
};

export const ANALYTICS_EXPORT_TABS = Object.keys(TABS);
